// Packages and components
import React, { createContext, useContext, useState } from 'react';

import SHDThemeOverride from '@ui/ThemeOverride/SHDThemeOverride';
import SHDTray from '@ui/Tray/SHDTray';

// Types and interfaces
import type { ReactNode } from 'react';
import type { ShowTrayOptions, TrayContextProps } from '@ui/Tray/SHDTray.d';

// Context
const TrayContext = createContext<TrayContextProps | undefined>(undefined);

export const TrayProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // States
  const [isTrayOpen, setIsTrayOpen] = useState(false);
  const [trayContent, setTrayContent] = useState<ReactNode | null>(null);
  const [trayOnClose, setTrayOnClose] = useState<(() => void) | undefined>();

  // Methods
  const showTray = (content: React.ReactNode, options?: ShowTrayOptions) => {
    const { onClose } = options || {};
    setTrayContent(content);
    setIsTrayOpen(true);
    setTrayOnClose(() => onClose);
  };

  const hideTray = () => {
    setIsTrayOpen(false);
    setTrayContent(null);
    trayOnClose && trayOnClose();
    setTrayOnClose(undefined);
  };

  return (
    <TrayContext.Provider value={{ showTray, hideTray }}>
      {children}

      {isTrayOpen && (
        <SHDThemeOverride full={false}>
          <SHDTray onClose={trayOnClose}>{trayContent}</SHDTray>
        </SHDThemeOverride>
      )}
    </TrayContext.Provider>
  );
};

export const useTray = () => {
  const context = useContext(TrayContext);
  if (context === undefined) {
    throw new Error('useTray must be used within a TrayProvider');
  }
  return context;
};
